import type {
  BuildingMaterialType,
  BuildingTypeEnum,
  GarageLocalizationType,
  MarketType,
  NeighborhoodType,
  OfferCategory,
  OfferType,
  PlotTypeEnum,
  PropertyConditionType,
  SellerType,
  ServiceUserTypeEnum,
} from "@prisma/client";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import * as R from "ramda";
import type {
  OfferDetailsExt,
  OffersResult,
  VersionResult,
} from "../../types/offers";
import { serializeError } from "../storeUtils";
import { type PaginationState, createFiltersStateFromFilter } from "./common";

type FiltersStateKeys = keyof FiltersState;
export const FILTER_COMMON_TYPES: FiltersStateKeys[] = [
  "type",
  "priceFrom",
  "priceTo",
  "areaFrom",
  "areaTo",
  "markType",
  "selType",
];

export type FilterSort =
  | "addedAtDesc"
  | "addedAtAsc"
  | "priceDesc"
  | "priceAsc"
  | "updatedAtDesc"
  | "areaDesc"
  | "areaAsc";

export type FiltersState = {
  filter?: number;
  type?: OfferType;
  priceFrom?: number;
  priceTo?: number;
  areaFrom?: number;
  areaTo?: number;
  rooms?: number[];
  floors?: number[];
  buildType?: BuildingTypeEnum[];
  selType?: SellerType;
  markType?: MarketType;
  priceMFrom?: number;
  priceMTo?: number;
  numFloorsFrom?: number;
  numFloorsTo?: number;
  buildYearFrom?: number;
  buildYearTo?: number;
  materialType?: BuildingMaterialType[];
  search?: string;
  placeId?: number[];
  placeLvl?: string[];
  placeDist?: number;
  plotFrom?: number;
  plotTo?: number;
  plotType?: PlotTypeEnum[];
  neighType?: NeighborhoodType[];
  minPlotSide?: number;
  maxPlotSide?: number;
  garLoc?: GarageLocalizationType[];
  useType?: ServiceUserTypeEnum[];
  bounds?: number[];
  isGreatOffer?: boolean;
  discountTo?: number;
  zoom?: number;
  view?: string;
  conds?: PropertyConditionType[];
  category?: OfferCategory[];
};

interface StateType {
  offers: OffersResult | null;
  offersPending: boolean;
  offerSearch: string;
  cart: any[];
  total: number;
  filters: FiltersState;
  loadedFilters: FiltersState | null;
  pagination: PaginationState | null;
  itemVersions: Record<number, VersionResult[]>;
  detailsPending: boolean;
  details: Record<number, OfferDetailsExt>;
  error: any;
}

const initialState: StateType = {
  offers: null,
  offersPending: false,
  offerSearch: "",
  cart: [],
  total: 0,
  filters: {},
  loadedFilters: null,
  pagination: null,
  itemVersions: {} as Record<number, VersionResult[]>,
  details: {} as Record<number, OfferDetailsExt>,
  detailsPending: false,
  error: "",
};

const OffersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    // HAS ERROR

    hasError(state: StateType, action) {
      state.error = serializeError(action.payload);
      state.offersPending = false;
      state.detailsPending = false;
    },

    // GET PRODUCTS
    getOffersPending: (
      state,
      action: PayloadAction<{ invalidatePreviousResults?: boolean }>,
    ) => {
      state.offersPending = true;
      if (action.payload.invalidatePreviousResults) {
        state.offers = null;
      }
    },
    getOffersResult: (state, action: PayloadAction<OffersResult>) => {
      state.offers = action.payload;
      state.offersPending = false;
      if (action.payload.filter) {
        const filters = createFiltersStateFromFilter(action.payload.filter);
        state.loadedFilters = filters;
        state.filters = filters;
      }
    },

    filterUpdate(state, action: PayloadAction<FiltersState>) {
      if (Object.keys(action.payload).length === 0) {
        state.filters = R.mergeRight(
          R.pick(FILTER_COMMON_TYPES, state.filters),
          {
            type: undefined,
          },
        );
      } else if (action.payload.type) {
        state.filters = R.mergeRight(
          R.pick(FILTER_COMMON_TYPES, state.filters),
          action.payload,
        );
      } else {
        state.filters = R.mergeRight(state.filters, action.payload);
      }
    },

    loadedFiltersUpdate(state, action: PayloadAction<FiltersState>) {
      state.loadedFilters = R.clone(action.payload);
      state.filters = R.clone(action.payload);
    },

    paginationUpdate(state, action: PayloadAction<PaginationState>) {
      state.pagination = R.clone(action.payload);
    },

    //  FILTER Reset
    filterReset(state) {
      if (state.loadedFilters) {
        state.filters = R.clone(state.loadedFilters);
      }
    },

    getOfferDetailsPending: (state) => {
      state.detailsPending = true;
    },
    getOfferDetails(state: StateType, action: PayloadAction<OfferDetailsExt>) {
      state.details[action.payload.id] = action.payload;
      state.detailsPending = false;
    },
  },
});
export const {
  hasError,
  getOffersResult,
  getOffersPending,
  filterUpdate,
  filterReset,
  loadedFiltersUpdate,
  paginationUpdate,
  getOfferDetails,
  getOfferDetailsPending,
} = OffersSlice.actions;

export default OffersSlice.reducer;
